import React, { useEffect, useRef, useState } from 'react'
import { Draggable } from "@fullcalendar/interaction"
import { jsRequest, getRequest, postRequest, pathname } from "@react/helpers/fetch"
import { useCalendar }                    from './useCalendar'
import { CALENDAR_VIEWS, workorderModalPath } from '@helpers/javascript/calendar_helper'
import { confirmScheduleConflict } from './conflictConfirm'

import EventCard from './eventCard'

const OpenEventsList = () => {
  const openEventsListRef = useRef(null)
  const draggableRef      = useRef(null)

  const { state, setState, TOOLS, fetchMoreOpenEvents } = useCalendar()
  const [suggest, setSuggest] = useState(null) // { workorderId, title, loading, items, anchor }

  useEffect(() => {
    if (!state.openEvents)          return
    if (!state.calendar)            return
    if (!openEventsListRef.current) return

    draggableRef.current = new Draggable(openEventsListRef.current, {
      itemSelector: ".draggable-event",
      appendTo: document.querySelector('.calendar-right'),
      eventData: function (eventEl) {
        const info = JSON.parse(eventEl.getAttribute('data-info'))
        let duration = info.duration
        let create = false
        let extendedProps = { info: info }
        return {
          duration,
          create,
          extendedProps,
        };
      },
    }, []);

    return () => {
      if (draggableRef.current) {
        draggableRef.current.destroy()
        draggableRef.current = null
      }
    };
  }, [state.openEvents, state.calendar])

  const openWorkorder = ev => {
    showModal('#show-workorder-modal')
    jsRequest(workorderModalPath(ev.info))
  }

  // ── Suggestions: best (technician, slot) pairs from the optimizer rules ──
  const fetchSuggestions = (ev, e) => {
    e.stopPropagation()
    const workorderId = ev.info.workorder_id
    if (suggest && suggest.workorderId === workorderId) return setSuggest(null) // toggle

    // Anchor the panel to the card being suggested, clamped to the viewport.
    const card   = e.currentTarget.closest('.top-event')
    const rect   = card ? card.getBoundingClientRect() : { left: 20, bottom: 120 }
    const width  = Math.min(480, window.innerWidth - 20)
    const anchor = {
      left: Math.max(10, Math.min(rect.left, window.innerWidth - width - 10)),
      top:  rect.bottom + 6,
    }

    setSuggest({ workorderId, title: ev.info.title, loading: true, items: [], anchor })
    getRequest(pathname({ path: '/calendar/suggest', format: 'json', params: { workorder_id: workorderId } }))
      .then(res => setSuggest({ workorderId, title: ev.info.title, loading: false, items: res.suggestions || [], anchor }))
      .catch(() => setSuggest(null))
  }

  const applySuggestion = (item, confirmed = false) => {
    const body = {
      event: {
        workorder_id:       suggest.workorderId,
        resourceId:         String(item.technician_id),
        new_date_planned:   item.starts_at,
        confirmed_conflict: confirmed,
      }
    }
    postRequest({ url: pathname({ path: '/calendar/update_drop', format: 'json' }), body }).then(res => {
      if (res.conflict) {
        if (confirmScheduleConflict(res.conflict)) applySuggestion(item, true)
        return
      }
      if (res.unprocessed) return
      setState(prevState => {
        const openEvents = (prevState.openEvents || []).filter(wo => wo.info.workorder_id !== suggest.workorderId)
        const events     = [...(prevState.events || [])]
        const idx        = events.findIndex(ev => res.event.info.workorder_id === ev.info?.workorder_id)
        if (idx !== -1) events[idx] = res.event
        else events.push(res.event)
        return {
          ...prevState,
          openEvents,
          openEventsTotal: Math.max((prevState.openEventsTotal || 0) - 1, 0),
          events,
        }
      })
      setSuggest(null)
    })
  }

  if (!state.calendar)   return null
  if (!state.openEvents) return null

  const isDraggable = state.calendar.currentData.viewSpec.type !== CALENDAR_VIEWS.agenda
  const total       = state.openEventsTotal || state.openEvents.length
  const hasMore     = state.openEvents.length < total

  return (
    <div className="open-event-list-wrapper">
      <div className="open-events-count text-muted small px-2">
        {I18n.t('calendar.open_pool_count', { shown: state.openEvents.length, total: total })}
      </div>

      {suggest && (
        <div className="suggest-panel" style={{ left: suggest.anchor.left, top: suggest.anchor.top }}>
          <div className="suggest-panel__title">
            {I18n.t('calendar.suggest_title')} — <b>{suggest.title}</b>
            <span className="suggest-panel__close" onClick={() => setSuggest(null)}>✕</span>
          </div>
          {suggest.loading && <div className="suggest-panel__empty">…</div>}
          {!suggest.loading && suggest.items.length === 0 && (
            <div className="suggest-panel__empty">{I18n.t('calendar.suggest_empty')}</div>
          )}
          {!suggest.loading && suggest.items.map((item, index) => (
            <div className="suggest-panel__row" key={index}>
              <span className="suggest-panel__dot" style={{ backgroundColor: item.technician_color }} />
              <span className="suggest-panel__when">{item.label_date} · {item.label_time}</span>
              <span className="suggest-panel__who">{item.technician_name}</span>
              {item.distance_km != null && (
                <span className="suggest-panel__km">{I18n.t('calendar.suggest_km', { km: Math.round(item.distance_km) })}</span>
              )}
              <button type="button" className="btn btn-sm suggest-panel__apply" onClick={() => applySuggestion(item)}>
                {I18n.t('calendar.suggest_apply')}
              </button>
            </div>
          ))}
        </div>
      )}

      <div ref={openEventsListRef} className='open-events-list my-2'>
        {state.openEvents.map(ev => {
          return (
            <div className={`fc-event top-event ${isDraggable ? 'draggable-event' : ''}`}
              style={{borderLeft: `4px solid ${ev.borderColor}`}}
              key={ev.info.workorder_id}
              data-info={JSON.stringify(ev.info)}
              onClick={() => openWorkorder(ev)}
            >
              {TOOLS.suggestPlanning && (
                <span
                  className = "suggest-trigger"
                  title     = {I18n.t('calendar.suggest_button')}
                  onClick   = {e => fetchSuggestions(ev, e)}
                >
                  <i className="fas fa-magic"></i>
                </span>
              )}
              <EventCard
                info            ={ev.info}
                showDate        ={false}
                showDuration
                externalCard
              />
            </div>
          )
        })}
      </div>
      {hasMore && (
        <button type="button" className="btn btn-sm btn-default w-100 mb-2" onClick={fetchMoreOpenEvents}>
          {I18n.t('calendar.open_pool_load_more')}
        </button>
      )}
    </div>
  )
}

export default OpenEventsList
